import { useEffect } from 'react';
import { getApiClient } from '@signalvote/api-client';
import { every, skip, useRefresh } from '@signalvote/data';
import type { TrialEmailType } from '@signalvote/shared';
import { defineQuery, defineWrite, edit, requireSelectedCampaign } from '@/lib/data';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

/** Billing configuration (publishable key + price details, with the campaign's beta coupon applied). */
const billingConfig = defineQuery({
  key: () => ['billing', 'config'],
  reads: 'GET /api/billing/config',
  fetch: (_: undefined, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.billing.getConfig();
  },
});

export function useBillingConfig() {
  return billingConfig(undefined);
}

/** The selected campaign's subscription. Stripe's webhooks change it, so no write set from this page names it. */
export const billingStatus = defineQuery({
  key: (_: { poll?: number | false }) => ['billing', 'status'],
  reads: 'GET /api/billing/status',
  refetchInterval: ({ poll }) => poll ?? false,
  fetch: (_, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.billing.getStatus();
  },
});

/**
 * Current subscription status for the selected campaign, polled while `refetchInterval` is set (the
 * post-payment complete page). Checkout and the Stripe portal run in another tab and land through
 * webhooks, so the status is asked again whenever this tab regains focus.
 */
export function useBillingStatus(options?: { refetchInterval?: number | false }) {
  const refresh = useRefresh();
  useEffect(() => {
    const onFocus = () => void refresh(billingStatus, every);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);
  return billingStatus({ poll: options?.refetchInterval });
}

/** Create or retrieve a Stripe subscription. Returns clientSecret for Stripe Elements confirmation. */
export const useSubscribe = defineWrite({
  send: () => api.billing.subscribe(),
});

/** Create a Stripe Customer Portal session and redirect to it (the PastDue banner's payment-method update). */
export const useCreatePortal = defineWrite({
  send: (params: { returnUrl?: string }) => api.billing.createPortal({ returnUrl: params.returnUrl ?? null }),
  onSuccess: (data) => {
    window.location.href = data.url;
  },
});

/** Toggle paywall on/off (platform admin only), and carry the new status into the selected campaign in the store. */
export const useTogglePaywall = defineWrite({
  send: (enabled: boolean) => api.billing.togglePaywall({ enabled }),
  onSuccess: (data) => {
    const { selectedCampaign, selectCampaign } = useCampaignStore.getState();
    if (selectedCampaign) selectCampaign({ ...selectedCampaign, subscriptionStatus: data.subscriptionStatus });
  },
});

/** The beta loyalty coupon attached to the selected campaign (platform admin only), or null when none is (204). */
const betaPricing = defineQuery({
  key: () => ['billing', 'beta-pricing'],
  reads: 'GET /api/billing/beta-pricing',
  fetch: (_: undefined, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.betaPricing.get();
  },
});

export function useBetaPricing() {
  return betaPricing(undefined);
}

/** Attach a Stripe Coupon ID as beta loyalty pricing (platform admin only). */
export const useAttachBetaPricing = defineWrite({
  send: (couponId: string) => api.betaPricing.put({ couponId }),
});

/** Remove beta loyalty pricing from the selected campaign (platform admin only); refused once a subscription is active. */
export const useRemoveBetaPricing = defineWrite({
  send: () => api.betaPricing.delete(),
});

const trialEmailAudit = defineQuery({
  key: () => ['billing', 'trial-emails'],
  reads: 'GET /api/billing/trial-emails',
  fetch: (_: undefined, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.billing.getTrialEmails();
  },
});

/**
 * The trial email audit (sent-at timestamps + formatted trial end) for the selected campaign. The
 * API refuses non-platform-admins, so callers pass `enabled: false` for them.
 */
export function useTrialEmailAudit(options?: { enabled?: boolean }) {
  return trialEmailAudit(options?.enabled === false ? skip : undefined);
}

/** Admin-override resend of a trial email (T3d / T1d / T1dAfter). The answer is the updated audit, shown at once. */
export const useSendTrialEmail = defineWrite({
  send: (type: TrialEmailType) => api.billing.sendTrialEmail({ type }),
  land: (audit) => [edit(trialEmailAudit, undefined, () => audit)],
});
